import { apiClient } from './client';
import { createCrudApi } from './crud';
import type { ApiSuccess } from '../types';

// ---- Categories (BOQ + SOW) ----
export interface Category {
  id: number;
  code: string;
  categoryType: string;
  name: string;
  parentId: number | null;
  status: 'active' | 'inactive';
  createdAt: string;
}
export interface CreateCategoryInput { name: string; parentId?: number | null; status?: 'active' | 'inactive'; }

export const boqCategoriesApi = createCrudApi<Category, CreateCategoryInput>('boq-categories');
export const sowCategoriesApi = createCrudApi<Category, CreateCategoryInput>('sow-categories');

// ---- BOQ Templates ----
export interface BoqTemplate {
  id: number; code: string; name: string; description: string | null; status: 'active' | 'inactive'; createdAt: string;
}
export interface CreateTemplateInput { name: string; description?: string; status?: 'active' | 'inactive'; }
const templateBase = createCrudApi<BoqTemplate, CreateTemplateInput>('boq-templates');
export const boqTemplatesApi = {
  ...templateBase,
  async getWithItems(id: number) {
    const { data } = await apiClient.get<ApiSuccess<BoqTemplate & { items: unknown[] }>>(`/boq-templates/${id}`);
    return data.data;
  },
  async setItems(id: number, items: unknown[]) {
    const { data } = await apiClient.put<ApiSuccess<unknown[]>>(`/boq-templates/${id}/items`, { items });
    return data.data;
  },
};

// ---- Client Rate Cards ----
export interface RateCard { id: number; code: string; clientId: number; name: string; status: 'active' | 'inactive'; createdAt: string; }
export interface CreateRateCardInput { clientId: number; name: string; status?: 'active' | 'inactive'; }
const rateCardBase = createCrudApi<RateCard, CreateRateCardInput>('rate-cards');
export const rateCardsApi = {
  ...rateCardBase,
  async setItems(id: number, items: unknown[]) {
    const { data } = await apiClient.put<ApiSuccess<unknown[]>>(`/rate-cards/${id}/items`, { items });
    return data.data;
  },
};

// ---- BOQ Upload (Excel/CSV → categories + template + rate card) ----
export interface BoqUploadRow {
  category?: string | null; sow?: string | null; description: string; unit?: string | null;
  quantity?: number | null; clientRate?: number | null; executionRate?: number | null; taxPercent?: number | null;
}
export interface BoqParseResult { rows: BoqUploadRow[]; detectedColumns: Record<string, string>; message?: string }
export interface CreateBoqUploadInput {
  clientId: number; name: string; geoScope: 'pan_india' | 'state'; stateIds?: number[]; rows: BoqUploadRow[];
}
export interface BoqUploadSummary {
  rateCardId: number; rateCardCode: string; templateId: number; templateCode: string;
  itemCount: number; boqCategoriesCreated: number; sowCategoriesCreated: number; geoScope: string; stateCount: number;
}
export const boqUploadApi = {
  parse: async (fileUrl: string) => (await apiClient.post<ApiSuccess<BoqParseResult>>('/boq-upload/parse', { fileUrl })).data.data,
  create: async (input: CreateBoqUploadInput) => (await apiClient.post<ApiSuccess<BoqUploadSummary>>('/boq-upload', input)).data.data,
};

// ---- Manufacturer Price Lists (Polycab/KEI/L&T… catalogue → separate rate card) ----
export interface PriceListRow {
  materialCode?: string | null; name: string; category?: string | null; hsnCode?: string | null; brand?: string | null; description?: string | null;
  unit?: string | null; mrp?: number | null; discountPercent?: number | null; rate?: number | null;
}
export interface PriceParseResult {
  source: 'excel' | 'ai'; configured?: boolean; rows: PriceListRow[]; detectedColumns: Record<string, string>;
  manufacturer?: string | null; message?: string;
}
export interface ManufacturerPriceList {
  id: number; code: string; manufacturer: string; title: string; effectiveDate: string | null; status: string; createdAt: string;
  items?: (PriceListRow & { id: number })[];
}
export interface CreatePriceListInput { manufacturer: string; title: string; effectiveDate?: string; rows: PriceListRow[] }
interface PricePaginated<T> { data: T[]; meta: { total: number; page: number; limit: number; totalPages: number } }

export const priceListsApi = {
  async list(params: Record<string, unknown> = {}): Promise<PricePaginated<ManufacturerPriceList>> {
    const { data } = await apiClient.get<ApiSuccess<ManufacturerPriceList[]>>('/manufacturer-price-lists', { params });
    return { data: data.data, meta: data.meta as PricePaginated<ManufacturerPriceList>['meta'] };
  },
  get: async (id: number) => (await apiClient.get<ApiSuccess<ManufacturerPriceList>>(`/manufacturer-price-lists/${id}`)).data.data,
  parse: async (fileUrl: string) => (await apiClient.post<ApiSuccess<PriceParseResult>>('/manufacturer-price-lists/parse', { fileUrl })).data.data,
  create: async (input: CreatePriceListInput) => (await apiClient.post<ApiSuccess<{ priceListId: number; code: string; manufacturer: string; itemCount: number }>>('/manufacturer-price-lists', input)).data.data,
  remove: async (id: number) => (await apiClient.delete<ApiSuccess<{ deleted: boolean }>>(`/manufacturer-price-lists/${id}`)).data.data,
};

// ---- BOQ Rate Lookup (rateless BOQ → pick rates from client rate cards + purchases) ----
export interface LookupBoqRow {
  description: string; unit?: string | null; category?: string | null; sow?: string | null; quantity?: number | null;
}
export interface RateMatch { rateCardCode: string; rateCardName: string; clientName: string | null; rate: number; unit: string | null }
export interface PurchaseInfo { rate: number; warehouseName: string | null; purchaseCode: string; date: string | null; purchaseCount: number; itemName: string; approx: boolean }
export interface LookupResultRow extends LookupBoqRow {
  matches: RateMatch[]; bestRate: number | null; recentPurchase: PurchaseInfo | null;
}
export const rateLookupApi = {
  parse: async (fileUrl: string) => (await apiClient.post<ApiSuccess<{ rows: LookupBoqRow[]; message?: string }>>('/rate-lookup/parse', { fileUrl })).data.data,
  lookup: async (rows: LookupBoqRow[]) => (await apiClient.post<ApiSuccess<{ rows: LookupResultRow[]; itemCount: number; matchedCount: number }>>('/rate-lookup/lookup', { rows })).data.data,
};
